// CSR-1978 — a coverage line's badge is a function of BOTH its own lifecycle
// and the parent policy's status. It used to read only the line's `removed`
// flag, so every live line on a Cancelled or Expired policy still rendered a
// green "Active" badge right below a red "Cancelled" policy header (CSR-1937
// cases 3, 6, 7). Mirrors `AssetStatusBadge` / `statusForAsset`, which already
// reconcile an asset row against its parent policy on the Assets tab.

import { cn } from "@/lib/utils";
import type { CoverageStatus } from "@shared/schema";
import { isPolicyInForce } from "./policy-status";

export type CoverageBadgeStatus = "active" | "inactive" | "removed";

const LABEL: Record<CoverageBadgeStatus, string> = {
  active: "Active",
  inactive: "Inactive",
  removed: "Removed",
};

// "Removed" keeps the darker grey established by CSR-1910 (it pairs with the
// card's muted background); "Inactive" borrows the lighter grey the Assets tab
// already uses for a line whose policy is no longer in force.
const STYLE: Record<CoverageBadgeStatus, string> = {
  active: "bg-emerald-100 text-emerald-700",
  inactive: "bg-gray-100 text-gray-600",
  removed: "bg-gray-200 text-gray-600",
};

export function CoverageStatusBadge({
  status,
  className,
}: {
  status: CoverageBadgeStatus;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
        STYLE[status],
        className,
      )}
    >
      {LABEL[status]}
    </span>
  );
}

/**
 * Pick the badge state from the coverage line's own lifecycle plus the parent
 * policy's status. `removed` is the line's own fact (the carrier dropped it, or
 * an agent removed it) and always wins; otherwise a line can only read Active
 * while the policy is in force.
 *
 * A missing policy status is a data gap — the policy header already surfaces it
 * as "—" — not evidence that the term lapsed, so the line stays Active rather
 * than inventing a lapse. `Policy.status` is non-nullable in practice.
 */
export function statusForCoverage(
  lineStatus: CoverageStatus | undefined,
  policyStatus: string | null | undefined,
): CoverageBadgeStatus {
  if (lineStatus === "removed") return "removed";
  if (!policyStatus) return "active";
  return isPolicyInForce(policyStatus) ? "active" : "inactive";
}
